/**
 * useDebugConfig Hook
 * 
 * Manages debug configuration:
 * - Remote logging setup
 * - Dev action logs persistence
 * - Debug log service subscription
 */

import { useState, useEffect } from 'react';
import { DevConfig } from '../../config/environment/devConfig';
import { getEnvironmentConfig } from '../../config/environment/environment';
import { gameLogger, LogLevel } from '../../utils/GameLogger';
import { storage } from '../services/StorageService';
import { DebugLogService } from '../services/DebugLogService';
import { chatHandler } from '../ui/contexts/ChatContext';
import { ChatMessage } from '../ui/HUD/ChatTypes';
import { bootSearchParams } from '../../config/boot/bootUrl';

interface UseDebugConfigReturn {
  showDevActionLogs: boolean;
  setShowDevActionLogs: (value: boolean) => void;
  logViewerVisible: boolean;
  setLogViewerVisible: (value: boolean) => void;
}

const REMOTE_LOG_LEVELS: Record<string, LogLevel> = {
  trace: LogLevel.TRACE,
  debug: LogLevel.DEBUG,
  info: LogLevel.INFO,
  warn: LogLevel.WARN,
  error: LogLevel.ERROR,
};

function resolveRemoteMinimumLevel(isLocalhost: boolean): LogLevel {
  if (typeof window !== 'undefined') {
    const requested = bootSearchParams()
      .get('remoteLogLevel')
      ?.toLowerCase();
    if (requested && REMOTE_LOG_LEVELS[requested] !== undefined) {
      return REMOTE_LOG_LEVELS[requested];
    }
  }

  // Ordinary local play keeps durable diagnostics focused on actionable
  // warnings/errors. Deployed QA retains informational lifecycle events.
  return isLocalhost ? LogLevel.WARN : LogLevel.INFO;
}

export const useDebugConfig = (): UseDebugConfigReturn => {
  const [showDevActionLogs, setShowDevActionLogs] = useState(() => {
    try {
      return storage.getBoolean('show_dev_action_logs');
    } catch (e) {
      return false;
    }
  });

  const [logViewerVisible, setLogViewerVisible] = useState(false);

  // Setup remote logging.
  // Use environment.apiUrl so the URL mirrors the page protocol (HTTPS on
  // PTR/prod, HTTP on localhost) and respects build-time VITE_API_URL
  // overrides set by deploy scripts. The previous DevConfig.apiUrl path
  // hardcoded `http://${hostname}:2567`, which the browser blocked as
  // mixed-content on any HTTPS deploy.
  useEffect(() => {
    // Playwright/E2E runs boot and tear down clients rapidly; remote logging
    // spams failed fetch console errors and offers no value under automation.
    const isPlaywright = !!(typeof window !== 'undefined' && (window as any).playwrightTest);
    if (DevConfig.enableRemoteLogging && !isPlaywright) {
      const environment = getEnvironmentConfig();
      const logUrl = `${environment.apiUrl}/logs`;
      const minimumLevel = resolveRemoteMinimumLevel(environment.isLocalhost);
      gameLogger.setRemoteLogging(logUrl, { minimumLevel });
      gameLogger.info('game', `[Game] - Remote logging enabled to: ${logUrl}`, {
        minimumLevel: LogLevel[minimumLevel],
      });
    } else {
      gameLogger.info('game', '[Game] - Remote logging is disabled (toggle in devConfig.ts)');
    }
  }, []);

  // Persist showDevActionLogs
  useEffect(() => {
    try {
      storage.setBoolean('show_dev_action_logs', showDevActionLogs);
    } catch (e) {
      gameLogger.warn('game', 'Failed to save dev action logging preference', { error: e });
    }
  }, [showDevActionLogs]);

  // Subscribe to debug logs
  useEffect(() => {
    const debugService = DebugLogService.getInstance();
    const unsubscribe = debugService.subscribe((msg: ChatMessage) => {
      chatHandler.addMessage(msg);
    });

    return () => unsubscribe();
  }, []);

  return {
    showDevActionLogs,
    setShowDevActionLogs,
    logViewerVisible,
    setLogViewerVisible
  };
};

export default useDebugConfig;
